import { useState, useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, Clock, Send, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sendLeadToCMS } from "@/lib/sendLeadToCMS";

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSending(true);

    const existing = JSON.parse(localStorage.getItem("tripsyfy_contacts") || "[]");
    existing.push({ ...form, submittedAt: new Date().toISOString() });
    localStorage.setItem("tripsyfy_contacts", JSON.stringify(existing));

    // Send to CMS in background (fire-and-forget, never blocks user)
    const nameParts = (form.name || "").trim().split(/\s+/);
    const svc = (form.service || "").toLowerCase();
    const tripType = svc.includes("flight") ? "flight"
                   : svc.includes("tour")   ? "tour"
                   : svc.includes("cruise")  ? "cruise"
                   : "contact";
    sendLeadToCMS({
      first_name:       nameParts[0] || "",
      last_name:        nameParts.slice(1).join(" "),
      email:            form.email,
      phone:            form.phone || "",
      trip_type:        tripType,
      departure_city:   "",
      destination_city: "",
      departure_date:   "",
      return_date:      "",
      message:          form.message || "",
      num_adults:       1,
      num_children:     0,
    });

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        console.log("[Contact] Email status:", data.emailStatus);
      }
    } catch (err) {
      console.warn("[Contact] API call failed, form still saved locally:", err.message);
    }

    setSending(false);
    setSubmitted(true);
    toast.success("Message sent! We'll get back to you soon.");
  };

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <SeoHead
        title="Contact Tripsyfy | Request a Travel Quote"
        description="Contact Tripsyfy by phone, WhatsApp, or email to request a customized travel quote for flights, tours, cruises, or group trips."
        canonical="https://tripsyfy.com/contact"
      />
      <Navbar />

      {/* Page header */}
      <section className="relative pt-28 pb-14 bg-primary" data-testid="contact-header">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Get in Touch</p>
          <h1 className="heading-xl text-white mb-3">Contact Us</h1>
          <p className="text-base text-white/70 max-w-lg leading-relaxed">
            Have a question or ready to plan your next adventure? Our team is here and happy to help.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-surface-subtle" data-testid="contact-content">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">

            {/* Contact form */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <div className="bg-white rounded-xl shadow-card p-6 sm:p-8">
                  {submitted ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
                      <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
                        <CheckCircle className="h-8 w-8 text-accent" />
                      </div>
                      <h3 className="heading-sm">Message Sent!</h3>
                      <p className="text-muted-foreground max-w-sm leading-relaxed">
                        Thank you for reaching out. Our travel experts will get back to you within 24 hours.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSubmitted(false);
                          setForm({ name: "", email: "", phone: "", service: "", message: "" });
                        }}
                        className="mt-2"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h2 className="heading-sm text-primary mb-6">Send us a Message</h2>
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                              id="name"
                              name="name"
                              autoComplete="name"
                              placeholder="John Doe"
                              value={form.name}
                              onChange={(e) => handleChange("name", e.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              autoComplete="email"
                              spellCheck={false}
                              placeholder="john@example.com"
                              value={form.email}
                              onChange={(e) => handleChange("email", e.target.value)}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              autoComplete="tel"
                              placeholder="+1 (555) 123-4567"
                              value={form.phone}
                              onChange={(e) => handleChange("phone", e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Service Interested In</Label>
                            <Select value={form.service} onValueChange={(val) => handleChange("service", val)}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a service" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="tours">Guided Tours</SelectItem>
                                <SelectItem value="flights">Flight Bookings</SelectItem>
                                <SelectItem value="cruises">Cruise Packages</SelectItem>
                                <SelectItem value="custom">Custom Itinerary</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="message">Message *</Label>
                          <Textarea
                            id="message"
                            placeholder="Tell us about your dream trip, travel dates, group size, or any questions..."
                            value={form.message}
                            onChange={(e) => handleChange("message", e.target.value)}
                            rows={5}
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          size="lg"
                          className="bg-accent text-white hover:bg-accent/90 active:scale-[0.97] gap-2 transition-[background-color,transform] duration-150"
                          disabled={sending}
                        >
                          {sending ? (
                            <><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…</>
                          ) : (
                            <><Send className="h-4 w-4" aria-hidden="true" /> Send Message</>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground">We respect your privacy. No spam, ever.</p>
                      </form>
                    </>
                  )}
                </div>
              </AnimatedSection>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-5">
              <AnimatedSection delay={0.1}>
                <div className="bg-white rounded-xl shadow-card p-6">
                  <h3 className="text-base font-bold text-primary mb-5">Get in Touch</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-xl bg-surface-soft flex items-center justify-center shrink-0">
                        <Phone className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-primary mb-0.5">Phone</p>
                        <a
                          href="tel:+19726661122"
                          className="text-sm text-muted-foreground hover:text-accent transition-colors"
                        >
                          +1 972 666 1122
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-xl bg-surface-soft flex items-center justify-center shrink-0">
                        <Mail className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-primary mb-0.5">Email</p>
                        <a
                          href="mailto:hello@tripsyfy.com"
                          className="text-sm text-muted-foreground hover:text-accent transition-colors"
                        >
                          hello@tripsyfy.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-xl bg-surface-soft flex items-center justify-center shrink-0">
                        <Clock className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-primary mb-0.5">Business Hours</p>
                        <p className="text-sm text-muted-foreground">
                          Mon – Fri: 9AM – 6PM EST<br />Sat: 10AM – 4PM EST
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.13}>
                <WhatsAppCTA variant="banner" />
              </AnimatedSection>

              <AnimatedSection delay={0.15}>
                <div className="bg-primary rounded-xl p-6">
                  <h3 className="text-base font-bold text-white mb-2">Need Urgent Help?</h3>
                  <p className="text-sm text-white/70 mb-4 leading-relaxed">
                    Our 24/7 emergency line is always available for active bookings.
                  </p>
                  <Button
                    className="w-full bg-accent text-white hover:bg-accent/90 gap-2"
                    asChild
                  >
                    <a href="tel:+19726661122">
                      <Phone className="h-4 w-4" /> +1 972 666 1122
                    </a>
                  </Button>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
